#version 300 es
precision mediump float;
precision mediump sampler2DShadow;

uniform vec4 uColor;
uniform vec3 uLightDir;
uniform vec3 uAmbient;
uniform float uRoughness;
uniform float uMetallic;
uniform vec3 uCameraPos;
uniform sampler2DShadow uShadowMap;

in vec3 vNormal;
in vec3 vWorldPos;
in vec4 vShadowCoord;
out vec4 fragColor;

const float PI = 3.14159265;

// GGX Normal Distribution Function
float distributionGGX(float NdotH, float a2) {
  float d = NdotH * NdotH * (a2 - 1.0) + 1.0;
  return a2 / (PI * d * d);
}

// Schlick Fresnel approximation
vec3 fresnelSchlick(float cosTheta, vec3 F0) {
  float t = 1.0 - cosTheta;
  float t2 = t * t;
  return F0 + (1.0 - F0) * (t2 * t2 * t);
}

// Schlick-GGX geometry (single direction)
float geometrySchlickGGX(float NdotX, float k) {
  return NdotX / (NdotX * (1.0 - k) + k);
}

// PCF shadow sampling (3x3 kernel)
float calcShadow() {
  vec3 proj = vShadowCoord.xyz / vShadowCoord.w;
  // Map from [-1,1] to [0,1]
  proj = proj * 0.5 + 0.5;

  // Outside shadow map bounds = fully lit
  if (proj.x < 0.0 || proj.x > 1.0 || proj.y < 0.0 || proj.y > 1.0 || proj.z > 1.0) {
    return 1.0;
  }

  // PCF 3x3
  float texelSize = 1.0 / 2048.0;
  float shadow = 0.0;
  for (int x = -1; x <= 1; x++) {
    for (int y = -1; y <= 1; y++) {
      vec3 sampleCoord = vec3(
        proj.x + float(x) * texelSize,
        proj.y + float(y) * texelSize,
        proj.z - 0.002 // bias to prevent shadow acne
      );
      shadow += texture(uShadowMap, sampleCoord);
    }
  }
  return shadow / 9.0;
}

void main() {
  vec3 N = normalize(vNormal);
  vec3 V = normalize(uCameraPos - vWorldPos);
  vec3 L = normalize(uLightDir);
  vec3 H = normalize(V + L);

  float NdotL = max(dot(N, L), 0.0);
  float NdotV = max(dot(N, V), 0.001);
  float NdotH = max(dot(H, N), 0.0);
  float HdotV = max(dot(H, V), 0.0);

  vec3 albedo = uColor.rgb;

  // F0: dielectric=0.04, metal=albedo
  vec3 F0 = mix(vec3(0.04), albedo, uMetallic);

  // Cook-Torrance specular BRDF
  float a = uRoughness * uRoughness;
  float a2 = a * a;
  float NDF = distributionGGX(NdotH, a2);

  float r1 = uRoughness + 1.0;
  float k = (r1 * r1) / 8.0;
  float G = geometrySchlickGGX(NdotV, k) * geometrySchlickGGX(NdotL, k);

  vec3 F = fresnelSchlick(HdotV, F0);

  vec3 specular = (NDF * G * F) / max(4.0 * NdotV * NdotL, 0.001);

  // Energy conservation
  vec3 kD = (1.0 - F) * (1.0 - uMetallic);

  // Shadow
  float shadow = calcShadow();

  // Direct lighting with shadow
  vec3 Lo = (kD * albedo / PI + specular) * vec3(3.0) * NdotL * shadow;

  // Ambient (hemisphere: slightly brighter from above)
  float hemi = dot(N, vec3(0.0, 1.0, 0.0)) * 0.5 + 0.5;
  vec3 ambient = uAmbient * albedo * mix(0.6, 1.0, hemi) * (1.0 - uMetallic * 0.4);

  vec3 color = ambient + Lo;

  // Reinhard tone mapping
  color = color / (color + vec3(1.0));

  // Gamma correction
  color = pow(color, vec3(1.0 / 2.2));

  fragColor = vec4(color, uColor.a);
}
